import React, { useMemo } from "react";
import { Modal } from "@pd/pd-ui-kit";
import { format, parse } from "date-fns";
import { useTranslation } from "react-i18next";

import Preloader from "~/components/Preloader";
import {
  RequisiteIProps,
  useAddNewRequisites,
  useGetItemRequisites,
  useUpdateRequisite,
} from "~/entity/AvtovazRequisites";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { useNotify } from "~/shared/hooks/useNotify";

import RequisitesForm, { RequisitesFormValues } from "./RequisitesForm";
import { defaultValueField } from "./option";

const requisitesFormId = "RequisitesForm";

enum ModalState {
  edit = "Edit",
  create = "Create",
}

interface RequisitesModalFormIProps {
  reFetchTable?: () => void;
}

const RequisitesModalForm = ({ reFetchTable }: RequisitesModalFormIProps) => {
  const { t } = useTranslation();
  const setDataReplace = useSetLocationData(true);
  const { openModal, currentId } = useGetLocationData();

  const isEdit = openModal === ModalState.edit;
  const modalIsOpen = isEdit || openModal === ModalState.create;

  const { data: dataItem, isLoading } = useGetItemRequisites({
    id: isEdit && currentId ? currentId : null,
  });
  const { mutate: addMutate } = useAddNewRequisites();
  const { mutate: editMutate } = useUpdateRequisite();
  const { notifySuccess, notifyError } = useNotify();

  const handleCloseModal = () =>
    setDataReplace("openModal", "", "currentId", "");

  const onSuccessHandler = (content: string) => () => {
    notifySuccess(content);
    handleCloseModal();
    reFetchTable?.();
  };
  const onErrorHandler = (content: string) => () => {
    notifyError(content);
  };

  const submit = (data: RequisitesFormValues) => {
    const payload: RequisiteIProps = {
      ...data,
      ogrn_date: format(new Date(data.ogrn_date), "dd.MM.yyyy"),
    };

    if (isEdit) {
      editMutate(
        { data: payload, id: Number(currentId) },
        {
          onSuccess: onSuccessHandler(t("avtovaz_requisite.update_success")),
          onError: onErrorHandler(t("avtovaz_requisite.update_error")),
        },
      );
    } else {
      addMutate(payload, {
        onSuccess: onSuccessHandler(t("avtovaz_requisite.create_success")),
        onError: onErrorHandler(t("avtovaz_requisite.create_error")),
      });
    }
  };

  const defaultValues = useMemo<RequisitesFormValues>(() => {
    if (!isEdit || !dataItem) return defaultValueField;
    const rest = { ...(dataItem as RequisiteIProps) };
    delete rest.id;
    return {
      ...defaultValueField,
      ...rest,
      ogrn_date: rest.ogrn_date
        ? parse(rest.ogrn_date, "dd.MM.yyyy", new Date())
        : null,
    };
  }, [isEdit, dataItem]);

  // В режиме редактирования ждём загрузку реквизита, чтобы не мигали пустые поля
  const formReady = !isEdit || (!!dataItem && !isLoading);

  return (
    <Modal
      open={modalIsOpen}
      title={
        isEdit
          ? t("avtovaz_requisite.edit_title")
          : t("avtovaz_requisite.create_title")
      }
      onCancel={handleCloseModal}
      closable
      centered
      destroyOnHidden
      size="large"
      okButton={{
        htmlType: "submit",
        form: requisitesFormId,
        children: t("ui.save"),
      }}
    >
      {formReady ? (
        <RequisitesForm
          formId={requisitesFormId}
          defaultValues={defaultValues}
          submit={submit}
        />
      ) : (
        <Preloader />
      )}
    </Modal>
  );
};

export default RequisitesModalForm;
